import { useCallback, useState } from 'react'
import {
  RequestStatus,
  TaskActualWorkApprovalStatusRow,
  TaskActualWorkApprovalStatusSkelton,
  TaskActualWorkApprovalStatusTotalRow,
} from '../taskActualWorkApprovalStatus'
import { useTaskActualWorkApprovalRepository } from '../../../../services/adaptors/taskActualWorkApprovalStatusAdaptor'

export const useTaskActualWorkApprovalStatusData = () => {
  const [data, setData] = useState<TaskActualWorkApprovalStatusRow[]>([])
  const [totalRow, setTotalRow] =
    useState<TaskActualWorkApprovalStatusTotalRow | null>(null)
  const [loading, setLoading] = useState(true)
  const [closingStatus, setClosingStatus] = useState<{
    uuid: string | undefined
    closed: boolean
    closedAt: string | undefined
  }>({
    uuid: undefined,
    closed: false,
    closedAt: undefined,
  })
  const {
    fetchTaskActualWorkProjectClosing,
    fetchTaskActualWorkApprovalStatusInfo,
  } = useTaskActualWorkApprovalRepository()

  const fetchClosing = useCallback(
    async (projectUuid: string, date: string) => {
      const response = await fetchTaskActualWorkProjectClosing({
        projectUuid,
        date,
      })
      const closingData = response.json
      setClosingStatus({
        closed: closingData ? (closingData.closed ?? false) : false,
        uuid: closingData ? closingData.uuid : undefined,
        closedAt: closingData ? closingData.closedAt : undefined,
      })
      return response
    },
    []
  )

  const fetchRecords = useCallback(
    async (projectUuid: string, startDate: string, endDate: string) => {
      setLoading(true)

      const isInvalidDateRange = new Date(startDate) > new Date(endDate)

      const response = await fetchTaskActualWorkApprovalStatusInfo({
        projectUuid,
        startDate,
        endDate,
      })

      const sources: TaskActualWorkApprovalStatusSkelton[] = response.json
      const rows = sources.map(v => {
        if (isInvalidDateRange) {
          return {
            type: 'data',
            uuid: v.user?.uuid ?? v.uuid,
            code: v.user?.code ?? '',
            division: v.user?.division?.displayName ?? '',
            position: v.user?.position?.displayName ?? '',
            partnerMember: v.user?.partnerMember,
            companyName: v.user?.partnerCompanyName?.displayName ?? '',
            userIconUrl: v.user?.iconUrl,
            userName: v.user?.name ?? '',
            discrepancyBetweenActualWorkingHourAndTaskActualWork: undefined,
            discrepancyActualWorkingHour: undefined,
            discrepancyTaskActualWork: undefined,
            requestStatus: RequestStatus.NONE,

            scheduledWorkingDays: 0,
            absences: 0,
            workingDays: 0,
            totalWorkingTime: 0,
            totalActualWorkingHour: 0,
            requestedHour: 0,
            unRequestedHour: 0,

            actualWorkingDaysCount: 0,
            requestedDaysCount: 0,
            unRequestedDaysCount: 0,
            approvedDaysCount: 0,
            unApprovedDaysCount: 0,
            sentBackDaysCount: 0,

            actualWorkHourThisProject: 0,
            requestedHourThisProject: 0,
            unRequestedHourThisProject: 0,
            approvedHourThisProject: 0,
            unApprovedHourThisProject: 0,
            sentBackHourThisProject: 0,

            plannedManPercent: 0,
            actualManPercent: 0,
            plannedManHour: 0,
            actualManHour: 0,
          } as TaskActualWorkApprovalStatusRow
        }

        return {
          // userInfo
          type: 'data',
          uuid: v.user?.uuid ?? v.uuid,
          code: v.user?.code ?? '',
          division: v.user?.division?.displayName ?? '',
          position: v.user?.position?.displayName ?? '',
          partnerMember: v.user?.partnerMember,
          companyName: v.user?.partnerCompanyName?.displayName ?? '',
          userIconUrl: v.user?.iconUrl,
          userName: v.user?.name ?? '',
          discrepancyBetweenActualWorkingHourAndTaskActualWork:
            v.allProjects?.timeMismatchFlag,
          discrepancyActualWorkingHour:
            v.allProjects?.missingActualWorkingHourFlag,
          discrepancyTaskActualWork: v.allProjects?.missingTaskActualWorkFlag,
          requestStatus: v.allProjects?.requestStatus,
          // All Projects Info
          scheduledWorkingDays: v.allProjects?.scheduledWorkingDays,
          absences: v.allProjects?.absences,
          workingDays: v.allProjects?.workingDays,
          totalWorkingTime: v.allProjects?.totalWorkingTime,
          totalActualWorkingHour: v.allProjects?.totalActualWorkingHour,
          requestedHour: v.allProjects?.requestedHour,
          unRequestedHour: v.allProjects?.unRequestedHour,
          // This Project Request Date Info
          actualWorkingDaysCount:
            v.thisProjectRequestDate?.actualWorkingDaysCount,
          requestedDaysCount: v.thisProjectRequestDate?.requestedDaysCount ?? 0,
          unRequestedDaysCount:
            v.thisProjectRequestDate?.unRequestedDaysCount ?? 0,
          approvedDaysCount: v.thisProjectRequestDate?.approvedDaysCount ?? 0,
          unApprovedDaysCount:
            v.thisProjectRequestDate?.unApprovedDaysCount ?? 0,
          sentBackDaysCount: v.thisProjectRequestDate?.sentBackDaysCount ?? 0,
          // This Project Request Time Info
          actualWorkHourThisProject: v.thisProjectRequestTime?.actualWorkHour,
          requestedHourThisProject: v.thisProjectRequestTime?.requestedHour,
          unRequestedHourThisProject: v.thisProjectRequestTime?.unRequestedHour,
          approvedHourThisProject: v.thisProjectRequestTime?.approvedHour,
          unApprovedHourThisProject: v.thisProjectRequestTime?.unApprovedHour,
          sentBackHourThisProject: v.thisProjectRequestTime?.sentBackHour,
          // Resource Plan Info
          plannedManPercent: v.resourcePlan?.plannedManPercent,
          actualManPercent: v.resourcePlan?.actualManPercent,
          plannedManHour: v.resourcePlan?.plannedManHour,
          actualManHour: v.resourcePlan?.actualManHour,
        } as TaskActualWorkApprovalStatusRow
      })
      // Unnecessary, but may be reimplemented in the future.

      // const totalRow = new TaskActualWorkApprovalStatusTotalRow()
      // totalRow.totalActualWorkingHourNotEntered = rows
      //   .filter(r => r.type === TaskActualWorkApprovalStatusRowType.DATA)
      //   .filter(r => r.discrepancyActualWorkingHour).length
      // totalRow.totalTaskActualWorkNotEntered = rows
      //   .filter(r => r.type === TaskActualWorkApprovalStatusRowType.DATA)
      //   .filter(r => r.discrepancyTaskActualWork).length
      // totalRow.totalDiscrepancyBetweenActualWorkingHourAndTaskActualWork = rows
      //   .filter(r => r.type === TaskActualWorkApprovalStatusRowType.DATA)
      //   .filter(
      //     r => r.discrepancyBetweenActualWorkingHourAndTaskActualWork
      //   ).length

      // setTotalRow(totalRow)
      setData(rows)
      setLoading(false)
    },
    []
  )

  return {
    data,
    loading,
    fetchRecords,
    fetchClosing,
    closingStatus,
    setClosingStatus,
    totalRow,
  }
}
